import PhotolabTaskBuilder from "../PhotolabTaskBuilder";
import PhotolabTaskCollageMethod from "../PhotolabTaskCollageMethod";
import PhotolabTaskImageUrl from "../PhotolabTaskImageUrl";
import Creative from "../Creative";
import {creativeTimeoutPromise, defaultHandlerCatch, defaultHandlerResolver, promisifyImage} from "./helpers";
import {photolabTask, photolabWaitTask} from "../api";
import {creativeName} from "../../utils/creative";
import {logCreativeResult, logEvent, userEvents} from "../../utils/log";
import {extraKeys} from "../etc";

const safePrompts2 = [
  "hotify_safe_prompt_1",
  "hotify_safe_prompt_2",
];

const tasksCache = {};
const moderateCache = {};

function getResultUrlFromTask(task, resultsIndex = -1) {
  if (task.requestId) {
    if (resultsIndex > -1) {
      return task.results[resultsIndex].resultUrl;
    } else {
      return task.resultUrl;
    }
  } else {
    return task.result.url;
  }
}

// @<name> - processing input file (@male)
// #<n> - combo step result file (#2)
// #<n>[<m>] - combo step result from results node (#2[2])
// &<name> - creative file (&layout)
// +<name> - task result file (+crop)
function getFileBySignature(processing, creative, stepIndex, source) {
  const sourceType = source[0];
  const sourceName = source.length > 1 ? source.substring(1) : null;
  let file = {
    url: source,
  };

  if (sourceType === "@") {
    const fileName = sourceName || "0";
    file = processing.getFile(fileName);
  }
  else if (sourceType === "#") {
    const subResults = sourceName ? sourceName.match(/^\d+\[\d+\]$/i) : null;
    if (subResults !== null && subResults.length > 0) {
      file.url = getResultUrlFromTask(creative.getTask("s" + subResults.first()), subResults.last());
    } else {
      let targetStep = sourceName ? parseInt(sourceName) : stepIndex - 1;
      if (targetStep < 0) {
        file = getFileBySignature(processing, creative, stepIndex, "@");
      } else {
        file.url = getResultUrlFromTask(creative.getTask("s" + targetStep));
      }
    }
  }
  else if (sourceType === "&") {
    file.url = creative.getFile(sourceName);
  }
  else if (sourceType === "+") {
    file.url = creative.getTask(sourceName).resultUrl;
  }
  else {
    file.url = source;
  }

  return file;
}

function templateStep(processing, creative, stepIndex, stepConfig) {
  const templateParams = stepConfig.templateParams || {};

  if (templateParams.gender && templateParams.gender[0] === "#") {
    const genderTask = creative.getTask("s" + templateParams.gender.substring(1));

    if (genderTask) {
      templateParams.gender = genderTask.gender.value === "male" ? "man" : "woman";
    } else {
      delete templateParams.gender;
    }
  }

  templateParams.template_name = stepConfig.id;

  const taskConfigBuilder = new PhotolabTaskBuilder()
    .setLanguage(processing.language)
    .addMethod(new PhotolabTaskCollageMethod(templateParams));

  (stepConfig.images || [{src: "#"}]).forEach((image) => {
    const file = getFileBySignature(processing, creative, stepIndex, image.src || "#");
    const maskFile = getFileBySignature(processing, creative, stepIndex, image.maskSrc || "@mask_0");
    const altBody = (image.useAltBody && maskFile) ? maskFile.url : "";
    const fileName = altBody.split("/").pop();
    const hash = fileName.substring(0, fileName.lastIndexOf("."));

    taskConfigBuilder.addImage(new PhotolabTaskImageUrl(
      file.url + (hash.length ? "?" + hash : ""),
      file.rect || "",
      file.rotation || 0,
      file.flip || 0,
      altBody
    ));
  });

  const taskConfig = taskConfigBuilder.buildToJs();
  const taskCacheKey = JSON.stringify(taskConfig);
  const taskTimeout = stepConfig.getResultTimeout || 1000;
  const taskInterval = stepConfig.getResultInterval || 1000;

  if (!tasksCache[taskCacheKey]) {
    tasksCache[taskCacheKey] = photolabTask(taskConfig, {
      timeout: taskTimeout,
      interval: taskInterval,
    });
  }

  if (window.clientConfig.isDebug) {
    creative.setTaskConfig("s" + stepIndex, taskConfig);
  }

  const promises = [
    tasksCache[taskCacheKey],
  ];

  stepConfig.timeout && promises.push(creativeTimeoutPromise(stepConfig.timeout));

  function getTask() {
    return Promise.race(promises).then((taskResult) => {
      creative.setTask("s" + stepIndex, taskResult);

      if (typeof stepConfig.setAsFile === "string" && stepConfig.setAsFile.length > 0) {
        creative.setFile(stepConfig.setAsFile, taskResult.resultUrl);
      }

      return taskResult;
    }).catch((err) => {
      if (err.name === "PhotolabResponseError") {
        if (stepConfig.ignorePhotolabError && err.code === stepConfig.ignorePhotolabError.code) {
          let ignored = true;

          if (stepConfig.ignorePhotolabError.amount) {
            stepConfig.ignorePhotolabError.amount--;
            ignored = stepConfig.ignorePhotolabError.amount > 0;
          }

          if (ignored) {
            promises[0] = photolabWaitTask(err.requestId, taskTimeout, taskInterval);

            return getTask();
          }
        }
      }

      if (err.name === "PhotolabResponseError" && err.code === -1028) {
        if (stepConfig.skipOnMultifaceError) {
          const imageUrl = stepIndex === 0
            ? processing.getFile(0).url
            : getResultUrlFromTask(creative.getTask("s" + (stepIndex - 1)));

          const result = {
            skipped: "skipOnMultifaceError",
            result: {url: imageUrl}
          };

          creative.setTask("s" + stepIndex, result);
          return result;
        }

        if (stepConfig.fallbackId) {
          const newStepConfig = JSON.parse(JSON.stringify(stepConfig));
          newStepConfig.id = newStepConfig.fallbackId;
          delete newStepConfig.fallbackId;
          return templateStep(processing, creative, stepIndex, newStepConfig);
        }
      }

      delete tasksCache[taskCacheKey];
      throw err;
    });
  }

  return getTask();
}

function moderateImageHelper(imageUrl) {
  if (moderateCache[imageUrl] !== undefined) {
    return Promise.resolve(moderateCache[imageUrl]);
  }

  const taskConfig = new PhotolabTaskBuilder()
    .addMethod(new PhotolabTaskCollageMethod({"@configId": "hotify_nsfw_questions"}))
    .addImage(new PhotolabTaskImageUrl(imageUrl))
    .buildToJs();

  return photolabTask(taskConfig)
    .then((taskResult) => {
      const moderaionResult = {
        answers: {
          is_nude: taskResult.answers[0],
          is_naked: taskResult.answers[1],
          naked_boobs: taskResult.answers[2],
          has_nudity: taskResult.answers[3],
          has_porno: taskResult.answers[4],
          is_porno: taskResult.answers[5],
          is_pornography: taskResult.answers[6],
          porno: taskResult.answers[7],
          naked_woman_boobs_visible: taskResult.answers[8],
          has_explicit_nudity: taskResult.answers[9],
        },
        isAdult: taskResult.answers.filter((a) => a.toLowerCase() !== "no").length > 2,
      };

      moderateCache[imageUrl] = moderaionResult;
      return moderaionResult;
    });
}

/**
 * @param {Processing} processing
 * @param {Creative} creative
 */
function handler(processing, creative) {
  const steps = creative.getExtra(Creative.EXTRA_COMBO_STEPS);

  function waitChain(index) {
    return new Promise((resolve, reject) => {
      const step = steps[index];
      let stepHandler = null;

      switch (step.type || "template") {
        case "template": {
          stepHandler = templateStep(processing, creative, index, step);
          break;
        }
        default: {
          throw new Error(`Unrecognized combo step: '${step.type}'.`);
        }
      }

      return stepHandler
        .then((res) => steps[index + 1] ? waitChain(index + 1) : res)
        .then(resolve)
        .catch(reject);
    });
  }

  // const timeoutMs = creative.getExtra(
  //   Creative.EXTRA_PROCESSING_TIMEOUT,
  //   window.appConfig.processings.creativeTimeout
  // );

  return new Promise((resolve, reject) => {
    // Promise.race([
    //   creativeTimeoutPromise(timeoutMs),
    //   waitChain(0),
    // ])

    waitChain(0).then(() => {
      const resultImageUrl = creative.getFile("raw");
      const sourcePhotoUrl = processing.files.first().url;

      if (creative.group === "video") {
        return Promise.resolve()
          .then(() => {
            creative.markAsProcessed(resultImageUrl);
          })
          .then(defaultHandlerResolver(creative, resolve))
          .catch(defaultHandlerCatch(creative, reject));
      }

      // проверяем результат на наличие элементов nsfw характера
      // если наличие подтверждено и если на оригинале их нет запускаем заново процессинг
      // но с другим сидом и так пока не получим результат без элементов nsfw характера
      return moderateImageHelper(resultImageUrl).then((resultModerationResult) => {
        creative.setExtra("result_moderation_result", resultModerationResult);
        if (resultModerationResult.isAdult) {
          return moderateImageHelper(sourcePhotoUrl).then((sourceModerationResult) => {
            creative.setExtra("source_moderation_result", sourceModerationResult);

            if (sourceModerationResult.isAdult) {
              return promisifyImage(resultImageUrl)
                .then(() => creative.markAsProcessed(resultImageUrl));
            } else {
              const triesAmount = creative.getExtra("tries_by_moderation", 0);

              logEvent(userEvents.moderationVerdict, {
                template_id: creativeName(creative),
                result_image_url: resultImageUrl,
                source_image_url: sourcePhotoUrl,
                result_moderation_result: resultModerationResult,
                source_moderation_result: sourceModerationResult,
                combo_config: steps,
              });

              logCreativeResult(
                "bad_" + creativeName(creative),
                [{url: sourcePhotoUrl}],
                [{url: resultImageUrl}],
                false,
                {
                  gender: creative.getExtra(extraKeys.gender),
                  body: creative.getExtra(extraKeys.body),
                  stylization: creative.getExtra(extraKeys.stylization),
                  context_type: creative.getExtra(extraKeys.contextType),
                  is_safe_prompt: creative.getExtra("is_safe_prompt"),
                  result_moderation_result: resultModerationResult,
                  source_moderation_result: sourceModerationResult,
                  moderation_tries: (triesAmount + 1),
                  answers: processing.getExtra("answers"),
                  age_verdict: processing.getExtra("age_verdict"),
                  bodies_states: processing.getExtra("bodies_states"),
                  combo: creative.getExtra(Creative.EXTRA_COMBO_STEPS),
                }
              );

              const promptStepIndex = steps.length - 1; // todo отметить AI шаблон в конфигах

              if (triesAmount >= 1) {
                if (creative.getExtra("is_safe_prompt") === true) {
                  throw new CreativeModerationError("Something went wrong (CME)");
                }

                steps[promptStepIndex].templateParams["@configId"] = safePrompts2.random();
                creative.setExtra("is_safe_prompt", true);
              } else {
                steps[promptStepIndex].templateParams.seeds = Math.round(Math.random() * 100000);
              }

              creative.setExtra(Creative.EXTRA_COMBO_STEPS, steps);
              creative.setExtra("tries_by_moderation", triesAmount + 1);

              return handler(processing, creative);
            }
          });
        } else {
          return promisifyImage(resultImageUrl)
            .then(() => creative.markAsProcessed(resultImageUrl));
        }
      });
    })
      .then(defaultHandlerResolver(creative, resolve))
      .catch(defaultHandlerCatch(creative, reject));
  });
}

export class CreativeModerationError extends Error {
  constructor(message) {
    super(message);
    this.name = "CreativeModerationError";
  }
}

export default handler;
